import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useIntercom } from "react-use-intercom";
import { ICON_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { IntercomContext } from "../../contexts/IntercomContext";
import { useGetAccount } from "../api/account/useGetAccount";
import useWallet from "../wallet/useWallet";

export function useIntercomHook() {
  const { setShowIntercomSetupModal } = useContext(IntercomContext);
  const { boot, show, update, shutdown, hide, isOpen } = useIntercom();
  const { account } = useWallet();
  const { data: accountData } = useGetAccount();

  const [hasContact, setHasContact] = useState<boolean | null>(null);

  useEffect(() => {
    setHasContact(
      Boolean(account && accountData?.email_address && accountData?.intercom_hash)
    );
  }, [account, accountData?.email_address, accountData?.intercom_hash]);

  const bootConfig = useMemo(() => {
    const userId = account || "";
    const userHash = accountData?.intercom_hash;
    const actionColor = ICON_COLORS.one;
    const hideDefaultLauncher = true;
    const verticalPadding = SPACING.five;
    const horizontalPadding = SPACING.five;
    return {
      actionColor,
      hideDefaultLauncher,
      verticalPadding,
      horizontalPadding,
      ...(userHash &&
        (accountData.email_address || hasContact) && { userId, userHash }),
    };
  }, [account, accountData, hasContact]);

  useEffect(() => {
    boot(bootConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIntercomEmailUpdate = useCallback(() => {
    if (account && accountData?.email_address && accountData?.intercom_hash) {
      update({
        userHash: accountData.intercom_hash,
        email: accountData.email_address,
      });
    }
  }, [account, accountData, update]);

  const rebootIntercom = useCallback(() => {
    shutdown();
    boot(bootConfig);
    handleIntercomEmailUpdate();
    show();
  }, [boot, bootConfig, handleIntercomEmailUpdate, show, shutdown]);

  const handleIntercomButtonClicked = useCallback(() => {
    // we can create a contact manually
    // so we check if the account address has a contact then boot up intercom
    if (account && (hasContact || accountData?.email_address)) {
      rebootIntercom();
    } else {
      setShowIntercomSetupModal(true);
    }
  }, [
    account,
    accountData?.email_address,
    hasContact,
    rebootIntercom,
    setShowIntercomSetupModal,
  ]);

  return {
    bootConfig,
    rebootIntercom,
    hide,
    isOpen,
    hasContact,
    handleIntercomButtonClicked,
  };
}
